import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import * as R from 'ramda';
import Button from '@mui/material/Button';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import Tooltip from '@mui/material/Tooltip';
import { ButtonGroup, MenuItem, Typography } from '@mui/material';
import MDropdownMenuButton from 'common/MDropdownMenuButton';
import { arrayOf, func, number, shape, string } from 'prop-types';
import { format, parseISO } from 'date-fns';
import PersistentDataGrid from 'common/PersistentDataGrid';
import { AuthContext } from 'authentication/AuthContext';
import { useUserRolePermissions } from 'hooks';
import { formatDateForExport } from 'utils/helpers/format';
import StatusChip from '../components/StatusChip';
import { distributionStatusTooltips, isEditableStatus } from '../utils';
import { DISTRIBUTIONS_DRAWER_MODE } from '../DistributionsDrawer/DistributionsDrawer';

const DistributionsGridView = ({ distributions, setSelectedDistribution, onDelete }) => {
  const navigate = useNavigate();
  const { isProductShippingManage } = useUserRolePermissions();
  const { affiliates, currentAffiliate } = useContext(AuthContext);
  const apiRef = useGridApiRef();

  const renderActionButtons = params => {
    const { distributionId, shipperName, inventoryOwnerName, status, parentShipmentId } =
      params.row;

    const isShipper = affiliates.some(({ customerNameShort }) =>
      R.equals(customerNameShort, shipperName)
    );

    const isSameUserShipper = currentAffiliate?.customerNameShort === shipperName;
    const isSameUserShipperInvOwner =
      currentAffiliate?.customerNameShort === inventoryOwnerName &&
      inventoryOwnerName === shipperName;
    const isShowParentMenu = isShipper || isSameUserShipper || isSameUserShipperInvOwner;

    const hasManagePermissions = R.all(R.equals(R.T()), [
      isProductShippingManage,
      isShipper,
      isEditableStatus(status),
    ]);

    if (hasManagePermissions) {
      return (
        <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
          <Button
            onClick={() =>
              setSelectedDistribution({
                distributionId,
                mode: DISTRIBUTIONS_DRAWER_MODE.EDIT,
              })
            }
          >
            Edit
          </Button>
          <MDropdownMenuButton>
            <MenuItem
              onClick={() =>
                setSelectedDistribution({
                  distributionId,
                  mode: DISTRIBUTIONS_DRAWER_MODE.VIEW,
                })
              }
            >
              View Distribution
            </MenuItem>
            {isShowParentMenu && (
              <MenuItem onClick={() => navigate(`../batches/shipment/${parentShipmentId}`)}>
                View Parent Shipment
              </MenuItem>
            )}
            <MenuItem onClick={() => onDelete('distribution', distributionId)}>
              Delete Distribution
            </MenuItem>
          </MDropdownMenuButton>
        </ButtonGroup>
      );
    }

    // View-only version
    if (isShowParentMenu) {
      return (
        <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
          <Button
            onClick={() =>
              setSelectedDistribution({
                distributionId,
                mode: DISTRIBUTIONS_DRAWER_MODE.VIEW,
              })
            }
            sx={{ minWidth: '80px' }}
          >
            View
          </Button>
          <MDropdownMenuButton>
            <MenuItem onClick={() => navigate(`../batches/shipment/${parentShipmentId}`)}>
              View Parent Shipment
            </MenuItem>
          </MDropdownMenuButton>
        </ButtonGroup>
      );
    }

    if (params.row.distributionId) {
      return (
        <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
          <Button
            onClick={() =>
              setSelectedDistribution({
                distributionId,
                mode: DISTRIBUTIONS_DRAWER_MODE.VIEW,
              })
            }
            sx={{ minWidth: '80px' }}
          >
            View
          </Button>
        </ButtonGroup>
      );
    }
    return null;
  };

  const columns = [
    {
      cellClassName: 'css-heap-redact',
      field: 'parentShipmentId',
      flex: 1,
      headerName: 'Shipment Id',
      renderCell: params => {
        const isShipper = affiliates.some(({ customerNameShort }) =>
          R.equals(customerNameShort, params.row.shipperName)
        );

        return isShipper ? (
          <Typography
            className="heap-parent-shipment-id-link"
            onClick={() =>
              navigate(`../batches/shipment/${R.prop('parentShipmentId', params.row)}/edit`)
            }
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            variant="text"
          >
            {R.prop('parentShipmentId', params.row)}
          </Typography>
        ) : (
          R.prop('parentShipmentId', params.row)
        );
      },
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'shipperName',
      flex: 1,
      headerName: 'Shipper',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'inventoryOwnerName',
      flex: 1,
      headerName: 'Inventory Owner',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'status',
      flex: 1,
      headerName: 'Status',
      renderCell: ({ row }) =>
        R.prop('status', row) && (
          <Tooltip placement="top-start" title={distributionStatusTooltips[R.prop('status', row)]}>
            <StatusChip status={R.prop('status', row)} />
          </Tooltip>
        ),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'locationName',
      flex: 1,
      headerName: 'Location',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'productName',
      flex: 1,
      headerName: 'Product Code',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'volume',
      flex: 0.7,
      headerName: 'Volume (bbls)',
      type: 'number',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'bookedVolume',
      flex: 0.7,
      headerName: 'Booked Volume',
      type: 'number',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'requestedDate',
      flex: 1,
      headerName: 'Requested Date',
      renderCell: ({ row }) =>
        R.prop('requestedDate', row)
          ? format(parseISO(R.prop('requestedDate', row)), 'MM/dd/yyyy')
          : null,
      valueFormatter: formatDateForExport(),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'action',
      flex: 1,
      headerName: 'Action',
      renderCell: renderActionButtons,
    },
  ];

  return (
    <PersistentDataGrid
      apiRef={apiRef}
      cellSelection
      columns={columns}
      disableRowSelectionOnClick
      getRowId={row => row.distributionId}
      hideFooter
      id="distributions"
      initialState={{
        aggregation: {
          model: {
            bookedVolume: 'sum',
            volume: 'sum',
          },
        },
        pinnedColumns: {
          left: [],
          right: ['action'],
        },
      }}
      pageSize={10}
      quickFilter
      rows={distributions}
      showFilterButton
    />
  );
};

DistributionsGridView.propTypes = {
  distributions: arrayOf(
    shape({
      bookedVolume: number.isRequired,
      chaining: number.isRequired,
      distributionId: number.isRequired,
      inventoryOwnerName: string.isRequired,
      locationName: string.isRequired,
      productName: string.isRequired,
      requestedDate: string.isRequired,
      shipperName: string.isRequired,
      status: string.isRequired,
      volume: number.isRequired,
    })
  ).isRequired,
  onDelete: func.isRequired,
  setSelectedDistribution: func.isRequired,
};

export default DistributionsGridView;
